// Example: src/utils/authFetch.js

// Import necessary functions from your other service files
import { getAccessToken, getRefreshToken, setAuthTokens, clearAuthTokens, refreshToken as apiRefreshToken } from '../service/tokenService'; // Assuming tokenService.js exists in the same directory or adjust path

// Flag to prevent multiple concurrent refresh attempts
let isRefreshing = false;
// Array to hold requests that failed while token was being refreshed
let refreshSubscribers = [];

// Function to add failed requests to the queue
const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

// Function to process the queue once refresh is done (or failed)
const onRefreshed = (newAccessToken) => {
  refreshSubscribers.map(cb => cb(newAccessToken)); // Pass new token (or null on failure)
  refreshSubscribers = []; // Clear queue
};

/**
 * Custom fetch wrapper that handles adding the Authorization header,
 * attempting token refresh on 401, and retrying the request.
 * @param {string} url The URL to fetch.
 * @param {object} [options={}] Fetch options (method, body, custom headers etc.).
 * @returns {Promise<any>} Resolves with the parsed JSON response or rejects on error.
 */
export async function authFetch(url, options = {}) {
  // Get current access token (from memory/state via tokenService)
  const token = getAccessToken();

  // Prepare headers
  const headers = {
    'Content-Type': 'application/json', // Default, adjust if needed
    'Accept': 'application/json',       // Default, adjust if needed
    ...options.headers,               // Allow overriding/adding headers
  };

  // Add Authorization header if token exists and it's not an auth request
  // Ensure API_BASE_URL is handled correctly if url is relative
  const isAuthUrl = url.includes('/auth/'); // Simple check, adjust if needed
  if (token && !isAuthUrl) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  options.headers = headers;

  try {
    // Initial fetch attempt
    let response = await fetch(url, options);


    // Check for 401 Unauthorized
    if (response.status === 401) {

      if (!isRefreshing) {
        // This is the first request to fail, initiate refresh
        isRefreshing = true; // Set flag

        try {
          const newAccessToken = await handleRefresh(); // Attempt refresh
          // Refresh successful, retry original request
          options.headers['Authorization'] = `Bearer ${newAccessToken}`;
          response = await fetch(url, options); // Retry the request
        } catch (refreshError) {
          // Refresh itself failed (logout/redirect likely handled in handleRefresh)
          throw refreshError; // Propagate the error from refresh failure
        } finally {
          isRefreshing = false; // Reset flag
        }
      } else {
        // Refreshing is already in progress, queue this request by waiting
        const newAccessToken = await new Promise((resolve) => {
          // Subscribe to the refresh completion
          subscribeTokenRefresh(resolve);
        });

        if (!newAccessToken) {
          // Refresh failed while waiting
          throw new Error("Token refresh failed while request was queued.");
        }

        // Refresh succeeded while waiting, retry with the new token
        options.headers['Authorization'] = `Bearer ${newAccessToken}`;
        response = await fetch(url, options); // Retry the request
      }
    } // End of 401 handling

    // Check for other non-OK statuses AFTER potential retry
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
      console.error(`API Error ${response.status} for ${url}:`, errorData);
      throw new Error(errorData.message || `HTTP error ${response.status}`);
    }

    // Handle potential non-JSON success responses (e.g., 204 No Content)
    const contentType = response.headers.get("content-type");
    if (response.status === 204 || !contentType || !contentType.includes("application/json")) {
      return response; // Return the raw response or handle appropriately
    }

    // Parse and return successful JSON response
    return await response.json();

  } catch (error) {
    console.error(`Workspace error for ${url}:`, error);
    // Re-throw the error to be caught by the calling component/function
    throw error;
  }
}


// Helper function within the wrapper module to handle the refresh logic
async function handleRefresh() {
  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    console.error("No refresh token available for refresh attempt.");
    clearAuthTokens(); // Logout
    // Maybe trigger a context update or navigation event here instead of direct window manipulation
    window.location.href = '/login'; // Redirect
    throw new Error("No refresh token");
  }

  try {
    // Call the dedicated refresh service function (uses raw fetch)
    const { accessToken: newAccessToken, refreshToken: newRefreshToken, user: user } = await apiRefreshToken(currentRefreshToken);
    // Store the new tokens using tokenService (updates memory & localStorage)
    setAuthTokens(newAccessToken, newRefreshToken);
    // Notify queued requests about the new token
    onRefreshed(newAccessToken);
    return newAccessToken;
  } catch (error) {
    console.error("Refresh token failed in handleRefresh:", error);
    // Clear tokens and redirect on final refresh failure
    clearAuthTokens();
    // Maybe trigger a context update or navigation event here
    window.location.href = '/login';
    // Notify queued requests that refresh failed
    onRefreshed(null);
    throw error; // Re-throw error
  }
}